// MultiValueInput.tsx
import React, { useState } from 'react';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputBase,
  Typography
} from '@mui/material';
import { ChipBox, MultiInputLabel, MultiInputWrapper } from './styled';
import { validate, ValidationRule } from '../../utils/validationUtils';
import Icons from '../icons';
import Placeholder from '../Placeholder/Placeholder';

interface MultiValueInputProps {
  values: string[];
  onAddValue: (value: string) => void;
  onRemoveValue: (index: number) => void;
  placeholder: string;
  label: string;
  validationType?: 'email' | 'phoneNumber' | 'text' | 'onlyText';
  validationRule?: ValidationRule[];
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const validationRules: { [key: string]: ValidationRule[] } = {
  email: [
    {
      type: 'pattern',
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email (e.g., example.com).'
    }
  ],
  phoneNumber: [
    {
      type: 'phoneNumber',
      message: 'Enter a valid phone number (7–10 digits).'
    }
  ],
  text: [
    {
      type: 'minLength',
      value: 1,
      message: 'Value must not be empty.'
    }
  ],
  onlyText: [
    {
      type: 'pattern',
      value: /^([A-Za-z]+)(,[A-Za-z]+)*$/,
      message:
        'Only alphabets are allowed (no spaces, numbers, or special characters).'
    }
  ]
};

const MultiValueInput = ({
  values,
  onAddValue,
  onRemoveValue,
  placeholder,
  label,
  validationType,
  validationRule,
  disabled,
  error,
  helperText,
  required=true,
}: MultiValueInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (inputError) {
      setInputError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const newValue = inputValue.trim();
      const rules =
        validationRule || validationRules[validationType || 'text'] || [];
      const validationError = validate(newValue, rules);

      if (validationError) {
        setInputError(validationError);
        return;
      }

      if (!values.includes(newValue)) {
        onAddValue(newValue);
      }
      setInputValue('');
      setInputError(null);
    }
    if (e.key === 'Backspace' && !inputValue && values.length > 0) {
      e.preventDefault();
      onRemoveValue(values.length - 1);
    }
  };

  const handleRemoveValue = (index: number) => {
    onRemoveValue(index);
  };

  const hasError = error || !!inputError;
  const displayHelperText = helperText || inputError;
  return (
    <FormControl fullWidth error={hasError} variant='outlined'>
      <MultiInputLabel
        required={required}
        shrink={focused || inputValue.length > 0 || values.length > 0}
        sx={{ fontWeight: 600 }}
      >
        {label}
      </MultiInputLabel>
      <MultiInputWrapper
        style={{
          border: hasError ? '1px solid red' : '1px solid #ccc',
          backgroundColor: disabled ? '#f5f5f5' : 'transparent',
          cursor: disabled ? 'not-allowed' : 'text'
        }}
      >
        {values.map((value, index) => (
          <ChipBox key={index}>
            <Typography
              variant='body2'
              style={{ fontWeight: 500, textWrap: 'nowrap' }}
            >
              {value}
            </Typography>
            <IconButton
              onClick={disabled ? undefined : () => handleRemoveValue(index)}
              size='small'
              style={{
                marginLeft: 0.5,
                padding: '2px',
                color: disabled ? '#e0e0e0' : 'grey.600'
              }}
              disabled={disabled}
            >
              <Icons.CloseCircleFilled />
            </IconButton>
          </ChipBox>
        ))}
        {placeholder &&
          values.length === 0 &&
          !focused &&
          inputValue.length === 0 && <Placeholder text={placeholder} />}
        <InputBase
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={focused ? placeholder : ''}
          disabled={disabled}
          style={{
            flex: '1 0 auto',
            marginBlock: '10px'
          }}
        />
      </MultiInputWrapper>

      {hasError && displayHelperText && (
        <FormHelperText>{displayHelperText}</FormHelperText>
      )}
    </FormControl>
  );
};

MultiValueInput.defaultProps = {
  validationType: 'text',
  disabled: false
};

export default MultiValueInput;
